"use client"

import React, { useState, useEffect, useCallback } from "react"
import { useParams } from "react-router-dom"
import { ChefHat, CheckCircle, AlertCircle, RefreshCw, Package, User, Utensils, Timer } from "lucide-react"
import { useLanguage } from "../../contexts/LanguageContext"
import { useToast } from "../../hooks/use-toast"
import { Button } from "../../components/ui/button"
import { Badge } from "../../components/ui/badge"
import PageHeader from "../../components/ui/dashboard/PageHeader"
import StatCard from "../../components/ui/dashboard/StatCard"
import DataCard from "../../components/ui/dashboard/DataCard"
import LoadingSpinner from "../../components/ui/dashboard/LoadingSpinner"
import { restaurantPortalAPI } from "../../services/restaurantApiClient"

interface OrderItem {
    foodItem: {
        _id: string
        name: string
        nameAr?: string
    }
    quantity: number
    price: number
    size?: {
        name: string
        nameAr?: string
        price: number
    }
    specialInstructions?: string
    customizations?: string[]
}

interface Order {
    _id: string
    orderNumber: string
    status: string
    orderType: string
    items: OrderItem[]
    table?: {
        _id: string
        tableNumber: string
    }
    customerInfo?: {
        name: string
        phone: string
    }
    createdAt: string
    pricing: {
        total: number
    }
}

const KitchenOrdersPage: React.FC = () => {
    const { restaurantId } = useParams()
    const { t, language, isRTL, dir } = useLanguage()
    const { toast } = useToast()

    const [orders, setOrders] = useState<Order[]>([])
    const [loading, setLoading] = useState(true)
    const [refreshing, setRefreshing] = useState(false)
    const [updatingOrder, setUpdatingOrder] = useState<string | null>(null)

    const fetchOrders = useCallback(async () => {
        if (!restaurantId) return

        try {
            const response = await restaurantPortalAPI.getOrders(restaurantId)
            const kitchenOrders = response.data.orders.filter(
                (order: Order) => order.status === "confirmed" || order.status === "preparing"
            )
            kitchenOrders.sort((a: Order, b: Order) =>
                new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
            )
            setOrders(kitchenOrders)
        } catch (error) {
            console.error("Error fetching orders:", error)
            toast({
                title: t("error"),
                description: t("failedToLoadOrders"),
                variant: "destructive",
            })
        } finally {
            setLoading(false)
            setRefreshing(false)
        }
    }, [restaurantId, t, toast])

    useEffect(() => {
        fetchOrders()
        const interval = setInterval(fetchOrders, 30000)
        return () => clearInterval(interval)
    }, [fetchOrders])

    const handleRefresh = () => {
        setRefreshing(true)
        fetchOrders()
    }

    const updateOrderStatus = async (orderId: string, newStatus: string) => {
        setUpdatingOrder(orderId)
        try {
            await restaurantPortalAPI.updateOrderStatus(orderId, { status: newStatus })
            toast({
                title: t("success"),
                description: t("orderStatusUpdated"),
            })
            fetchOrders()
        } catch (error) {
            console.error("Error updating order:", error)
            toast({
                title: t("error"),
                description: t("failedToUpdateStatus"),
                variant: "destructive",
            })
        } finally {
            setUpdatingOrder(null)
        }
    }

    const getMinutesSinceOrder = (createdAt: string) => {
        return Math.floor((Date.now() - new Date(createdAt).getTime()) / 60000)
    }

    const getTimeSinceOrder = (createdAt: string) => {
        const minutes = getMinutesSinceOrder(createdAt)
        if (minutes < 60) return `${minutes}m`
        const hours = Math.floor(minutes / 60)
        return `${hours}h ${minutes % 60}m`
    }

    const getUrgencyLevel = (createdAt: string) => {
        const minutes = getMinutesSinceOrder(createdAt)
        if (minutes > 20) return 'urgent'
        if (minutes > 10) return 'warning'
        return 'normal'
    }

    if (loading) {
        return <LoadingSpinner size="3" text={t("loadingOrders")} />
    }

    const confirmedOrders = orders.filter(o => o.status === "confirmed")
    const preparingOrders = orders.filter(o => o.status === "preparing")
    const totalItems = orders.reduce((sum, order) => sum + order.items.reduce((s, item) => s + item.quantity, 0), 0)

    return (
        <div className="space-y-6" dir={dir}>
            <PageHeader
                title={t("kitchenOrders")}
                subtitle={t("kitchenOrdersDescription")}
            >
                <Button
                    onClick={handleRefresh}
                    variant="outline"
                    disabled={refreshing}
                    size="sm"
                >
                    <RefreshCw className={`w-4 h-4 ${isRTL ? 'ml-2' : 'mr-2'} ${refreshing ? 'animate-spin' : ''}`} />
                    {t("refresh")}
                </Button>
            </PageHeader>

            {/* Stats */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                <StatCard
                    title={t("waitingToStart")}
                    value={confirmedOrders.length}
                    icon={AlertCircle}
                    color="bg-yellow-500"
                />
                <StatCard
                    title={t("currentlyPreparing")}
                    value={preparingOrders.length}
                    icon={ChefHat}
                    color="bg-orange-500"
                />
                <StatCard
                    title={t("totalOrders")}
                    value={orders.length}
                    icon={Package}
                    color="bg-blue-500"
                />
                <StatCard
                    title={t("totalItems")}
                    value={totalItems}
                    icon={Utensils}
                    color="bg-green-500"
                />
            </div>

            {/* Orders */}
            {orders.length === 0 ? (
                <DataCard
                    title={t("activeOrders")}
                    subtitle={t("ordersRequiringAttention")}
                    isEmpty={true}
                    emptyMessage={t("noOrdersInKitchen")}
                >
                    <div />
                </DataCard>
            ) : (
                <div className="space-y-4">
                    <div className="flex items-center justify-between">
                        <div>
                            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">{t("activeOrders")}</h2>
                            <p className="text-sm text-gray-500 dark:text-gray-400">{t("ordersRequiringAttention")}</p>
                        </div>
                    </div>
                    <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4">
                        {orders.map((order) => {
                            const isConfirmed = order.status === 'confirmed'
                            const urgency = getUrgencyLevel(order.createdAt)
                            const totalItemCount = order.items.reduce((sum, item) => sum + item.quantity, 0)

                            return (
                                <div
                                    key={order._id}
                                    className={`bg-white dark:bg-gray-800 rounded-xl border-2 overflow-hidden shadow-sm hover:shadow-md transition-shadow ${urgency === 'urgent' ? 'border-red-400 dark:border-red-600' :
                                        urgency === 'warning' ? 'border-orange-400 dark:border-orange-600' :
                                            isConfirmed ? 'border-yellow-400 dark:border-yellow-600' : 'border-blue-400 dark:border-blue-600'
                                        }`}
                                >
                                    {/* Header */}
                                    <div className={`px-4 py-3 ${isConfirmed
                                        ? 'bg-yellow-50 dark:bg-yellow-900/20'
                                        : 'bg-blue-50 dark:bg-blue-900/20'
                                        }`}>
                                        <div className="flex items-center justify-between">
                                            <div className="flex items-center gap-2">
                                                <span className="text-lg font-bold text-gray-900 dark:text-white">
                                                    #{order.orderNumber}
                                                </span>
                                                {order.table && (
                                                    <Badge variant="secondary" className="text-xs">
                                                        {t("table")} {order.table.tableNumber}
                                                    </Badge>
                                                )}
                                            </div>
                                            <div className="flex items-center gap-2">
                                                <Badge
                                                    variant={isConfirmed ? "outline" : "default"}
                                                    className={isConfirmed
                                                        ? "bg-yellow-100 text-yellow-800 border-yellow-300 dark:bg-yellow-900/30 dark:text-yellow-300"
                                                        : "bg-blue-100 text-blue-800 border-blue-300 dark:bg-blue-900/30 dark:text-blue-300"
                                                    }
                                                >
                                                    {isConfirmed ? t("confirmed") : t("preparing")}
                                                </Badge>
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-3 mt-2 text-sm">
                                            <span className={`flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${urgency === 'urgent' ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300' :
                                                urgency === 'warning' ? 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300' :
                                                    'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300'
                                                }`}>
                                                <Timer className="w-3 h-3" />
                                                {getTimeSinceOrder(order.createdAt)}
                                            </span>
                                            <span className="text-gray-500 dark:text-gray-400 flex items-center gap-1">
                                                <Package className="w-3 h-3" />
                                                {totalItemCount} {t("items")}
                                            </span>
                                        </div>
                                    </div>

                                    {/* Items */}
                                    <div className="p-4 space-y-2 max-h-64 overflow-y-auto">
                                        {order.items.map((item, index) => {
                                            const itemName = item.foodItem
                                                ? (language === 'ar' && item.foodItem.nameAr
                                                    ? item.foodItem.nameAr
                                                    : item.foodItem.name)
                                                : (item as any).name || t("unknownItem")
                                            const sizeName = item.size
                                                ? (language === 'ar' && item.size.nameAr
                                                    ? item.size.nameAr
                                                    : item.size.name)
                                                : ''
                                            return (
                                                <div
                                                    key={index}
                                                    className="flex items-start gap-3 p-2 bg-gray-50 dark:bg-gray-700/50 rounded-lg"
                                                >
                                                    <span className="flex items-center justify-center w-8 h-8 bg-red-600 text-white rounded-lg font-bold text-sm flex-shrink-0">
                                                        {item.quantity}x
                                                    </span>
                                                    <div className="flex-1 min-w-0">
                                                        <p className="font-medium text-gray-900 dark:text-white text-sm">
                                                            {itemName}
                                                        </p>
                                                        {sizeName && (
                                                            <p className="text-xs text-red-600 font-medium">{sizeName}</p>
                                                        )}
                                                        {item.customizations && item.customizations.length > 0 && (
                                                            <div className="flex flex-wrap gap-1 mt-1">
                                                                {item.customizations.map((custom, i) => (
                                                                    <span
                                                                        key={i}
                                                                        className="text-xs bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300 px-1.5 py-0.5 rounded"
                                                                    >
                                                                        {custom}
                                                                    </span>
                                                                ))}
                                                            </div>
                                                        )}
                                                        {item.specialInstructions && (
                                                            <div className="mt-1.5 flex items-start gap-1 text-xs text-red-600 dark:text-red-400">
                                                                <AlertCircle className="w-3 h-3 flex-shrink-0 mt-0.5" />
                                                                <span>{item.specialInstructions}</span>
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            )
                                        })}
                                    </div>

                                    {/* Customer Info */}
                                    {order.customerInfo?.name && (
                                        <div className="px-4 py-2 border-t border-gray-100 dark:border-gray-700">
                                            <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
                                                <User className="w-4 h-4" />
                                                <span>{order.customerInfo.name}</span>
                                            </div>
                                        </div>
                                    )}

                                    {/* Action */}
                                    <div style={{ padding: '12px', borderTop: '1px solid #e5e7eb', backgroundColor: '#f9fafb' }} className="dark:bg-gray-800 dark:border-gray-700">
                                        {isConfirmed ? (
                                            <button
                                                type="button"
                                                onClick={() => !updatingOrder && updateOrderStatus(order._id, "preparing")}
                                                disabled={updatingOrder === order._id}
                                                className="w-full h-11 bg-orange-600 hover:bg-orange-700 text-white rounded-lg text-sm font-semibold flex items-center justify-center gap-2 transition-colors disabled:opacity-70"
                                            >
                                                {updatingOrder === order._id ? (
                                                    <RefreshCw className="w-5 h-5 animate-spin" />
                                                ) : (
                                                    <ChefHat className="w-5 h-5" />
                                                )}
                                                <span>{updatingOrder === order._id ? t("updating") : t("startPreparing")}</span>
                                            </button>
                                        ) : (
                                            <button
                                                type="button"
                                                onClick={() => !updatingOrder && updateOrderStatus(order._id, "ready")}
                                                disabled={updatingOrder === order._id}
                                                className="w-full h-11 bg-green-600 hover:bg-green-700 text-white rounded-lg text-sm font-semibold flex items-center justify-center gap-2 transition-colors disabled:opacity-70"
                                            >
                                                {updatingOrder === order._id ? (
                                                    <RefreshCw className="w-5 h-5 animate-spin" />
                                                ) : (
                                                    <CheckCircle className="w-5 h-5" />
                                                )}
                                                <span>{updatingOrder === order._id ? t("updating") : t("markReady")}</span>
                                            </button>
                                        )}
                                    </div>
                                </div>
                            )
                        })}
                    </div>
                </div>
            )}
        </div>
    )
}

export default KitchenOrdersPage
